import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faFlask, faClock, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/Risk-Model-Process.png";
import vis from "../../assets/images/use-cases/Risk-Model-Dashboard.png";

const RiskModelRapidPrototyping = () => {
    return (
        <>
            <SEO
                title="Risk Model Rapid Prototyping: AI Digital Twin for Credit Risk | Nebulyx AI"
                description="Accelerate credit risk model development from months to weeks using AI Digital Twins. Secure sandboxes, pre-built ML templates, and visual model building tools for faster hypothesis testing."
                keywords="AI digital twin, credit risk model, risk prototyping, ML templates, regulatory compliance, IFRS 9, model validation"
                canonicalUrl="https://nebulyx.ai/use-cases/risk-model-rapid-prototyping"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faFlask}/>
                                            &nbsp;
                                            Finance
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How can we reduce credit risk model development from months to weeks?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Create secure sandbox environments with direct data access, pre-built ML templates, and visual model building tools to accelerate hypothesis testing and validation before production deployment.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">75%</span>
                                <span className="stat-label">Faster Development</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faCheckCircle} className="stat-icon"/>
                                <span className="stat-value">Hours</span>
                                <span className="stat-label">To Working Prototype</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">Built-in</span>
                                <span className="stat-label">Regulatory Compliance</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Risk model development cycles take 6-12 months</li>
                                            <li>Siloed handoffs between modellers, data teams, and compliance</li>
                                            <li>Limited ability to test hypotheses quickly with real data</li>
                                            <li>Heavy regulatory documentation burden slows iteration</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Secure sandboxes with pre-connected banking data sources</li>
                                            <li>Data virtualisation layer - direct access without replication</li>
                                            <li>Pre-built ML templates for common risk model patterns</li>
                                            <li>Visual canvases for rapid prototyping and validation</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Working prototypes in hours, not months</li>
                                            <li>Full data lineage and audit trail from day one</li>
                                            <li>Multiple paths to production deployment</li>
                                            <li>Regulatory-ready documentation auto-generated</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connects directly to banking, risk, and external data sources to build a Data Object Graph that maps the complete model development lifecycle from hypothesis to production.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Access Data</strong>
                                    <span>Spin up sandbox with pre-connected core banking and risk data sources</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Explore Hypothesis</strong>
                                    <span>Query relevant data, run profiling to check quality and distributions</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Develop Model</strong>
                                    <span>Use ML templates or custom Python, train on development sample</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Test & Export</strong>
                                    <span>Validate on holdout sample, export specs for production team</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests data from core banking systems, risk data lakes, and external APIs to model
                                            the complete credit risk landscape. Data Object Graph maps data sources to risk models
                                            to lineage, enabling full traceability for regulatory review.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Risk Model Prototyping Workspace</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Delivers fully functional model development screens for risk analysts to use directly.
                                            Teams can connect data sources, build models, review performance metrics, and export
                                            production-ready packages without waiting for IT provisioning.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>


                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Core Banking System Data</li>
                                            <li>Risk Data Lake (Parquet/SQL)</li>
                                            <li>Transaction Streams (Kafka)</li>
                                            <li>External Credit Bureau APIs</li>
                                            <li>Historical Default Outcomes</li>
                                            <li>Macroeconomic Indicators</li>
                                            <li>Collateral Valuations</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>IFRS 9 PD Model templates</li>
                                            <li>Mortgage LGD Model patterns</li>
                                            <li>Behavioural scoring frameworks</li>
                                            <li>Custom Python/R model support</li>
                                            <li>Feature importance analysis</li>
                                            <li>Model performance metrics (AUC, KS, Gini)</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Economic stress scenarios</li>
                                            <li>Portfolio concentration risk</li>
                                            <li>Variable selection impact</li>
                                            <li>Holdout sample validation</li>
                                            <li>Cross-validation stability</li>
                                            <li>Regulatory threshold compliance</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Discovery</h3>
                                    <p>
                                        By modelling the risk development process as a <strong>Data Object Graph</strong>, we can
                                        provide risk modellers with instant access to governed data sources and pre-built templates.
                                        <strong> Interactive prototyping can reduce model development cycles from 6-12 months to 4-6 weeks</strong>
                                        {" "}while maintaining full regulatory compliance. Multiple productionisation paths, from
                                        documentation handoff to container deployment-ensure models reach production faster.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Optimal Strategy Found</span>
                                        <span className="discovery-value">Rapid Prototyping</span>
                                        <span className="discovery-note">75% faster with built-in compliance</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Need to Accelerate Risk Model Development?</h2>
                            <p>
                                If lengthy development cycles and siloed handoffs are slowing your risk modelling capability,
                                let's build your prototyping environment together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default RiskModelRapidPrototyping;